import QRCode from 'qrcode';
import bwipjs from 'bwip-js'; // Import bwip-js for PDF417 support

// Convert base32 string to Uint8Array
const base64ToUint8Array = (base64) => {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for(let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
};

const generateTOTP = async (secret, intervalSize, length, timeStampMs) => {
    try {
        // Calculate time step
        const timeStep = Math.floor(timeStampMs / intervalSize);

        // Convert secret to proper format
        const keyData = base64ToUint8Array(secret);

        // Import the secret key
        const key = await crypto.subtle.importKey(
            'raw',
            keyData,
            { name: 'HMAC', hash: 'SHA-1' },
            false,
            ['sign']
        );

        // Create time bytes (big-endian)
        const timeBytes = new Uint8Array(8);
        let remainingTime = timeStep;
        for (let i = 7; i >= 0; i--) {
            timeBytes[i] = remainingTime & 0xff;
            remainingTime >>= 8;
        }

        // Generate HMAC
        const signature = await crypto.subtle.sign(
            'HMAC',
            key,
            timeBytes
        );

        // Get the byte array of the signature
        const hmacResult = new Uint8Array(signature);

        // Get offset
        const offset = hmacResult[hmacResult.length - 1] & 0xf;

        // Generate 4-byte code
        const code = ((hmacResult[offset] & 0x7f) << 24) |
            ((hmacResult[offset + 1] & 0xff) << 16) |
            ((hmacResult[offset + 2] & 0xff) << 8) |
            (hmacResult[offset + 3] & 0xff);

        // Get the specified number of digits
        const modulus = Math.pow(10, length);
        return (code % modulus).toString().padStart(length, '0');
    } catch (error) {
        console.error('TOTP generation error:', error);
        throw error;
    }
};

const BarcodeType = {
    QR_CODE: 14,
    PDF_417: 11
};

const generateBarcodeData = async (totpSecrets, intervalSize, pattern) => {
    const timeStampMs = Date.now();
    const timeStampS = Math.floor(timeStampMs / 1000);

    // Create a map for O(1) lookups by index
    const secretMap = new Map();
    totpSecrets.forEach(secret => {
        secretMap.set(secret.index, secret);
    });

    let processedPattern = pattern;

    // Process TOTP placeholders
    for (let i = 0; i < totpSecrets.length; i++) {
        const secret = secretMap.get(i);
        if (secret) {
            const totpValue = await generateTOTP(
                secret.secret,
                intervalSize,
                secret.length,
                timeStampMs
            );
            processedPattern = processedPattern.replace(
                `{totp_value_${i}}`,
                totpValue
            );
        }
    }

    // Replace timestamp placeholders
    processedPattern = processedPattern.replace(
        '{totp_timestamp_seconds}',
        timeStampS.toString()
    );
    processedPattern = processedPattern.replace(
        '{totp_timestamp_millis}',
        timeStampMs.toString()
    );

    return processedPattern;
};

export const generateTOTPBarcode = async (barcodeType, totpSecrets, intervalSize, pattern) => {
    try {
        const content = await generateBarcodeData(totpSecrets, intervalSize, pattern);

        switch (barcodeType) {
            case BarcodeType.QR_CODE:
                const qrDataUrl = await QRCode.toDataURL(content, {
                    errorCorrectionLevel: 'M',
                    width: 300,
                    margin: 1
                });
                return qrDataUrl.split(',')[1];

            case BarcodeType.PDF_417:
                // Use bwip-js to generate PDF417
                return new Promise((resolve, reject) => {
                    bwipjs.toBuffer({
                        bcid: 'pdf417',
                        text: content,
                        scale: 3,
                        height: 10,
                        includetext: false,
                    }, function(err, png) {
                        if (err) {
                            console.error('PDF417 generation error:', err);
                            reject(err);
                        } else {
                            const base64String = png.toString('base64');
                            resolve(base64String);
                        }
                    });
                });

            default:
                throw new Error(`Unsupported barcode type: ${barcodeType}`);
        }
    } catch (error) {
        console.error('Barcode generation error:', error);
        throw new Error(`Failed to generate barcode: ${error.message}`);
    }
};

export { BarcodeType };
